import { Button, Card, Col, Divider, Row, Space } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import IncentiveContext from "../../../context/incentive/context";
import IncentiveContextProvider from "../../../context/incentive/provider";
import DriverClassificationContext from "../../../context/driver-classification/context";
import DriverClassificationContextProvider from "../../../context/driver-classification/provider";
import { IUpdateIncentive } from "../../../models/incentive/request";
import EndPoints from "../../../services/end-points";
import { execute } from "../../../utils/helpers/execute";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import styles from "./style.module.scss";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(IncentiveContext);
  const driverClassificationContext = useContext(DriverClassificationContext);
  const { id } = useParams();

  const [driverClassificationOptions, setDriverClassificationOptions] = useState<any[]>([]);

  const methods = useForm<IUpdateIncentive>({
    defaultValues: {
      name: details?.name || "",
      description: details?.description || "",
      cost: details?.cost ? parseFloat(details.cost) : 0,
      status: details?.status || 1,
      driverClassificationId: details?.driverClassificationId || 0,
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  const statusOptions = [
    { label: t("active"), value: 1 },
    { label: t("inactive"), value: 2 },
  ];

  useEffect(() => {
    id && actions.getDetails(Number(id));
    driverClassificationContext.actions.getData();
  }, [id]);

  useEffect(() => {
    if (driverClassificationContext.list?.data) {
      const options = driverClassificationContext.list.data.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      setDriverClassificationOptions(options);
    }
  }, [driverClassificationContext.list]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const onSubmit = async (data: IUpdateIncentive) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          await EndPoints.incentive.updateIncentive(
            details.id,
            {
              ...data,
              description: data.description || null,
            }
          );
        } else {
          await EndPoints.incentive.createIncentive({
            ...data,
            description: data.description || null,
          });
        }
        navigate(-1);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  useEffect(() => {
    reset({
      name: details?.name || "",
      description: details?.description || "",
      cost: details?.cost ? parseFloat(details.cost) : 0,
      status: details?.status || 1,
      driverClassificationId: details?.driverClassificationId || 0,
    });
  }, [details]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details
              ? t("update_incentive")
              : t("add_incentive")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Main Information Section */}
                <Card title={t("main_information")} bordered={false}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <FieldBuilder
                        name="name"
                        label={t("name")}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <FieldBuilder
                        name="description"
                        label={t("description")}
                        input={{ type: "text-area", rows: 4 }}
                        rules={{ required: false }}
                      />
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <FieldBuilder
                        name="cost"
                        label={t("cost")}
                        input={{ type: "number" }}
                        suffix={t("currency")}
                        rules={{ required: true, min: 0 }}
                      />
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <FieldBuilder
                        name="driverClassificationId"
                        label={t("driver_classification")}
                        input={{
                          type: "select",
                          options: driverClassificationOptions,
                        }}
                        rules={{ required: true }}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Additional Settings Section */}
                <Card title={t("additional_settings")} bordered={false}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <FieldBuilder
                        name="status"
                        label={t("status")}
                        input={{
                          type: "select",
                          options: statusOptions,
                        }}
                        rules={{ required: true }}
                      />
                    </Col>
                  </Row>
                </Card>
              </Space>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const IncentiveForm = () => {
  return (
    <DriverClassificationContextProvider>
      <IncentiveContextProvider>
        <Form />
      </IncentiveContextProvider>
    </DriverClassificationContextProvider>
  );
};

export default IncentiveForm;
